/// <reference path="../services/commprefs-services.d.ts" />

import angular from "angular";

export interface ICommValueMap {
  direct_mail: CommPrefsApi.ICommunicationValue;
  email: CommPrefsApi.ICommunicationValue;
  text: CommPrefsApi.ICommunicationValue;
  voice: CommPrefsApi.ICommunicationValue;
  [key: string]: CommPrefsApi.ICommunicationValue;
}

interface IDirectiveScope extends ng.IScope {
  commValueMap: ICommValueMap;
  hideTypes: number[];
  preferences: CommPrefsApi.ICommunicationSummaryResource;

  onCatChannelToggle(
    catChan: CommPrefsApi.ICommunicationChannel,
    catType: CommPrefsApi.ICommunicationType
  ): void;
  unsubscribeAll(channelKey: string): void;
  channelCheckboxCount(channelKey: string): number;
  shouldShowCommType(commType: CommPrefsApi.ICommunicationType): boolean;
  doesCommTypeSupportTextChannel(
    commType: CommPrefsApi.ICommunicationType
  ): boolean;
  showSmsTermsDialog(): void;
}

const directiveName = "ltCommunicationPreferences";
const directive = [
  () => {
    return {
      restrict: "A",
      scope: {
        channelCheckboxLabel: "@",
        channels: "=",
        commValueMap: "=",
        hideCategoryHeaders: "=",
        hideTypes: "=",
        preferences: "=",
      },
      template: `
<!-- Temporarily hiding the Alerts and Account Notifications section (category id = 1) -->
<div id="commprefs-comm-category-{{category.Id}}" class="commprefs-category" ng-repeat="category in preferences.Categories" ng-if="category.Id !== 1">

	<h3 class="h2 h-static" ng-if="!hideCategoryHeaders">{{category.Name}}</h3>

	<div id="commprefs-comm-type-{{catType.Id}}" ng-repeat="catType in category.Types" ng-if="shouldShowCommType(catType)">
		<h4 class="h5" ng-if="hideCategoryHeaders || catType.Name !== category.Name">
			{{catType.Name}}
		</h4>

		<p>
			<span ng-bind-html="catType.Description"></span>
			<a href="{{catType.SampleImageUrl}}" target="_blank" ng-if="catType.SampleImageUrl">View Sample</a>
		</p>

		<p ng-if="doesCommTypeSupportTextChannel(catType)">
			By selecting text, you are opting in to receive alerts via text message at the phone number selected under Communication Methods
			and agree to <a role="button" href="#" ng-click="showSmsTermsDialog($event)">SMS Terms & Conditions</a>.
		</p>

		<div class="commprefs-type-channels form-group form-group-spacer">
			<span ng-class="'commprefs-comm-channel-' + channel.Key" ng-repeat="channel in catType.Channels">
				<label for="commprefs-comm-type-{{catType.Id}}-{{channel.Key}}-checkbox" class="c-input c-checkbox-sm">
					<input id="commprefs-comm-type-{{catType.Id}}-{{channel.Key}}-checkbox" type="checkbox" ng-model="channel.Preference.OptIn" ng-change="onCatChannelToggle(channel, catType)">
					<span class="c-indicator"></span>
					{{channelCheckboxLabel ? channelCheckboxLabel : channel.DisplayName}}
				</label>
			</span>
		</div>
	</div>

	<hr ng-if="!hideCategoryHeaders" />

</div>

<hr ng-if="hideCategoryHeaders" />

<div class="commprefs-global-unsubscribe c-inputs-stacked form-group form-group-spacer">
	<div ng-class="'commprefs-comm-channel-' + channel.Key"
	     ng-repeat="channel in channels"
	     ng-if="commValueMap[channel.Key].CanBeUnsubscribed">

	     <label for="commprefs-global-unsubscribe-{{channel.Key}}-checkbox" class="c-input c-checkbox-sm">
			<input id="commprefs-global-unsubscribe-{{channel.Key}}-checkbox"
		       type="checkbox"
			   ng-click="unsubscribeAll(channel.Key)"
			   ng-model="commValueMap[channel.Key].Unsubscribed">
			<span class="c-indicator"></span>
			Unsubscribe from all Life Time {{channel.DisplayName | uppercase}} communications
		</label>

	</div>
</div>

<div id="smsTermsModal" class="modal" role="dialog" aria-describedby="SMS Terms and Conditions">
	<div class="modal-dialog" role="document">
		<div class="modal-content">
		<div class="modal-header">
			<button type="button" class="close" data-dismiss="modal" aria-label="Close">
				<span aria-hidden="true">×</span>
			</button>
			<h4 class="h2 h-static m-t-1 modal-title">Text Message Program Terms & Conditions</h4>
		</div>
		<div class="modal-body">
			<p>
				<strong>Effective Date:</strong> 9/08/2021
			</p>
			<p>
				Life Time’s text message service or program (“Text Program”) is operated by Life Time, Inc. 
				including its affiliates and subsidiaries (“Life Time,” “we,” “us,” or “our”). 
				These Text Message Program Terms and Conditions (“Text Program Terms”) govern your use of the Text Program. 
				As applicable, our <a href="https://my.lifetime.life/policy/terms-of-use.html" target="_blank">Terms of Use</a> and <a href="https://my.lifetime.life/policy/privacy-policy.html" target="_blank">Privacy Policy</a> also apply to your use of the Text Message Program. 
				The Text Program Terms are in addition to and not in lieu of the <a href="https://my.lifetime.life/policy/terms-of-use.html" target="_blank">Terms of Use</a> and the <a href="https://my.lifetime.life/policy/privacy-policy.html" target="_blank">Privacy Policy</a>.
			</p>
			<p>
				Your participation in the Text Program constitutes your acceptance of these Text Program Terms 
				and your express consent to receive communications to your mobile phone and/or device (including, 
				without limitation, by means of interactive 2-way communications), even if your mobile phone number 
				is on the national or a state do-not-call registry. If you do not agree to these Text Program Terms, 
				you must immediately discontinue your use of the Text Program by following the opt-out instructions below.
			</p>
			<p>
				Our <a href="https://my.lifetime.life/policy/terms-of-use.html" target="_blank">Terms of Use</a> include an Arbitration Agreement, which will govern any dispute between you and us. 
				As described in the <a href="https://my.lifetime.life/policy/terms-of-use.html" target="_blank">Terms of Use</a>, subject to certain limited exceptions, this Arbitration Agreement will:
			</p>
			<ul>
				<li>Eliminate your right to a trial by jury; and</li>
				<li>Substantially affect your rights, including preventing you from bringing, joining or participating 
				in class or consolidated proceedings.</li>
			</ul>
			<p>
				<strong>
					By participating in the Text Program, you accept, and agree to be bound by, the terms of the Arbitration Agreement, 
					which shall be incorporated herein by reference, except as otherwise restricted by law.
				</strong>
			</p>
			<p>
				<strong>Canadian Residents Only</strong>: Laws of certain provinces of Canada limit or prohibit required arbitration and waivers 
				of class actions; accordingly, the Arbitration Agreement as applied to this Text Program is limited or restricted, 
				if at all, for applicable Canadian residents to the extent of applicable Canadian laws.
			</p>
			<h4 class="h5 m-b-1">Table of Contents</h4>
			<ul>
				<li>These Terms May Change</li>
				<li>Text Program Basics and Fees</li>
				<li>Opt-In to Text Program</li>
				<li>Requirements/Valid Mobile Phone Number</li>
				<li>Cancellation/Opting-Out of Text Program</li>
				<li>Help</li>
				<li>Participating Carriers</li>
				<li>Indemnification</li>
				<li>Limitation on Liability</li>
			</ul>
			<h4 class="h5 m-b-1">These Terms May Change</h4>
			<p>
				We may amend these Text Program Terms and modify or cancel the Text Program 
				or any of its features without notice.
			</p>
			<h4 class="h5 m-b-1">Text Program Basics and Fees</h4>
			<p>
				Standard message and data rates may apply. These rates may be charged by your mobile carrier. 
				Life Time does not charge for the Text Program, but you are responsible for all charges and fees 
				associated with text messaging that may be imposed by your mobile carrier. Text messages may be sent 
				using an automatic telephone dialing system and using short message service (SMS) or multi-media messaging 
				service (MMS) technology. Your consent to receive text messages is not required as a condition of purchasing 
				any goods or services from Life Time.
			</p>
			<h4 class="h5 m-b-1">Opt-In to Text Program</h4>
			<p>
				If you would like to participate in the Text Program, then you must opt-in to receive text messages 
				by opting in on the Preference Center Page under Communication Preferences: Communication Methods. 
				You may need to confirm your consent, such as by following the instructions contained in 
				an initial text message we send. If you have opted-in to the Text Program, Life Time (or a third-party on behalf 
				of Life Time, in which case you expressly consent to Life Time sharing your provided mobile number 
				to such third-party for this purpose) may provide transactional or marketing messages via text messages 
				(including, without limitation, by means of interactive 2-way communication whereby you can exchange information 
				with Life Time) through your wireless provider to the mobile phone number you provided.
			</p>
			<p>
				These messages may include, but are not limited to, alerts, updates, notifications, confirmations, 
				announcements, information, instruction, advice, promotions, offers or other content related to registrations, 
				reservations, appointments, reminders, cancellations, scheduling, access restrictions 
				(including club closures/limitations), urgencies or emergencies, club policies, memberships 
				(including, without limitation, initial enrollment and changes thereto), dues and fees, terms and conditions; 
				events, classes, sessions, programs, products, features and amenities, including but not limited to notifications 
				regarding youth or minor programs such as child center, summer camps, or school break programs; mobile, 
				online or other digital applications or offerings; product purchases/orders; shipping/delivery updates; 
				other goods, products or services (whether available in-club or online); health, fitness, nutrition, or wellness; 
				sales, discounts, promotions, complimentary or trial sessions; other marketing or advertising; 
				and other content that Life Time thinks will be of interest to you. You can expect to receive recurring 
				and non-recurring autodialed marketing and promotional messages (some of which may be sent via 
				an automated telephone dialing system) from Life Time.
			</p>
			<h4 class="h5 m-b-1">Requirements/Valid Mobile Phone Number</h4>
			<p>
				Consent to receiving these messages is not a condition of purchase. Further, you represent 
				that you are the account holder, subscriber and/or customary user for the mobile number you enroll 
				into the Text Program. You must provide us with a valid mobile phone number. If you change your mobile phone number, 
				you agree to opt out of the Text Program prior to changing your mobile phone number. You are responsible 
				for notifying Life Time immediately if you change your mobile number (see “Cancellation/Opting-Out of Text Program”
				for contact information). You must be at least 18 years of age or have a parent or legal guardian's permission 
				in order to participate.
			</p>
			<h4 class="h5 m-b-1">Cancellation/Opting-Out of Text Program</h4>
			<p>
				At any time, you may stop receiving texts by replying “STOP” to a text message from Life Time, 
				or by logging into Mylt.com and updating your Communication Preferences under My Account. 
				You will receive a one-time opt-out confirmation text message. For help, call 1-888-430-6432. Please note that this will 
				only opt you out of the specific texts or text messaging program associated with that number.
			</p>
			<h4 class="h5 m-b-1">Help</h4>
			<p>
				At any time, you may receive support regarding the Text Program by replying “HELP” to a text message 
				from Life Time. Please note that replying “HELP” will not opt you out of the Text Program. 
				You may stop receiving our texts as described above.
			</p>
			<h4 class="h5 m-b-1">Participating Carriers</h4>
			<p>
				The content of Text Program text messages may not be available and viewable on all carriers. 
				Life Time and/or mobile carriers are not liable for delayed or undeliverable messages.
			</p>
			<h4 class="h5 m-b-1">Indemnification</h4>
			<p>
				In addition to any other indemnification obligations you have as set forth in the <a href="https://my.lifetime.life/policy/terms-of-use.html" target="_blank">Terms of Use</a>, 
				you agree to indemnify, defend, and hold Life Time and its owners, directors, officers, agents, 
				and employees, harmless from any loss, liability, claim, demand, damage, or cost, 
				including reasonable attorneys’ fees, made by any third party due to or arising out of your use 
				of the Text Program or from you providing us with a mobile phone number that is not your own or 
				for which you are not an authorized account holder or user.
			</p>
			<h4 class="h5 m-b-1">Limitation on Liability</h4>
			<p>
				<strong>
					IN ADDITION TO ANY LIMITATIONS ON OUR LIABILITY AS SET FORTH IN THE 
					<a href="https://my.lifetime.life/policy/terms-of-use.html" target="_blank">TERMS OF USE</a>, 
					TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAWS, LIFE TIME IS NOT RESPONSIBLE 
					OR LIABLE IN ANY MANNER FOR FAILED, DELAYED, OR MISDIRECTED DELIVERY OF ANY INFORMATION 
					SENT THROUGH THE TEXT PROGRAM, ANY ERRORS IN SUCH INFORMATION, AND/OR ANY ACTION 
					YOU MAY OR MAY NOT TAKE IN RELIANCE ON THE INFORMATION OR TEXT PROGRAM.
				</strong>
			</p>
			<p>
				You can view our <a href="https://my.lifetime.life/policy/privacy-policy.html" target="_blank">Privacy Policy here</a>
			</p>
		</div>
			<div class="modal-footer">
				<button type="button" class="btn btn-sm btn-primary" data-dismiss="modal">Close</button>
			</div>
		</div>
	</div>
</div>
`,
      link: ($scope: IDirectiveScope) => {
        $scope.onCatChannelToggle = (
          catChan: CommPrefsApi.ICommunicationChannel,
          catType: CommPrefsApi.ICommunicationType
        ) => {
          if (catChan.Preference!.OptIn && !catType.OptInRequired) {
            // if the user manually opts into a channel on a non-opt-in-required category type, make sure the unsubscribe checkbox is in an off state
            $scope.commValueMap[catChan.Key].Unsubscribed = false;
          } else if (!catChan.Preference!.OptIn && catType.OptInRequired) {
            // If opting out and at least one channel is required to be opted-in, make sure another channel is opted in.
            let channelToOptIn: CommPrefsApi.ICommunicationChannel | undefined;
            for (const channel of catType.Channels) {
              if (channel !== catChan) {
                if (channel.Preference!.OptIn) {
                  // found another channel opted in, so nothing for us to do
                  channelToOptIn = undefined;
                  break;
                } else {
                  // this channel's opted out, so it's a candidate to be opted in
                  channelToOptIn = channel;
                }
              }
            }

            if (channelToOptIn) {
              channelToOptIn.Preference!.OptIn = true;
            }
          }
        };

        $scope.unsubscribeAll = (channelKey: string) => {
          angular.forEach(
            $scope.preferences.Categories,
            (cat: CommPrefsApi.ICommunicationCategory) => {
              angular.forEach(
                cat.Types,
                (type: CommPrefsApi.ICommunicationType) => {
                  if (!type.OptInRequired) {
                    // don't touch opt-in-required type channels (specifically Alerts & Account Notifications)
                    angular.forEach(
                      type.Channels,
                      (chan: CommPrefsApi.ICommunicationChannel) => {
                        if (chan.Key === channelKey) {
                          if (chan.Preference && chan.Preference.OptIn) {
                            chan.Preference.OptIn = false;
                          }
                        }
                      }
                    );
                  } else if (type.OptInRequired) {
                    // the text channel in Alerts & Account Notifications should be toggled
                    angular.forEach(
                      type.Channels,
                      (chan: CommPrefsApi.ICommunicationChannel) => {
                        if (chan.Key === "text" && chan.Key === channelKey) {
                          if (chan.Preference) {
                            chan.Preference.OptIn =
                              !$scope.commValueMap[chan.Key].Unsubscribed ??
                              false;
                          }
                        }
                      }
                    );
                  }
                }
              );
            }
          );
        };

        $scope.channelCheckboxCount = (channelKey: string) => {
          return getAllChannelsFromSummary().filter(
            (chan: CommPrefsApi.ICommunicationChannel) => {
              return chan.Key === channelKey;
            }
          ).length;
        };

        // Returns true if the specified comm type isn't in the list of comm types to hide
        $scope.shouldShowCommType = (
          commType: CommPrefsApi.ICommunicationType
        ) => {
          return (
            !$scope.hideTypes ||
            $scope.hideTypes.filter((commTypeId: number) => {
              return commType.Id === commTypeId;
            }).length === 0
          );
        };

        $scope.doesCommTypeSupportTextChannel = (
          commType: CommPrefsApi.ICommunicationType
        ) => {
          return (
            commType.Channels.filter(
              (chan: CommPrefsApi.ICommunicationChannel) => {
                return chan.Key === "text";
              }
            ).length > 0
          );
        };

        $scope.showSmsTermsDialog = () => {
          $("#smsTermsModal").modal();
        };

        function getAllChannelsFromSummary(): CommPrefsApi.ICommunicationChannel[] {
          const channels = new Array<CommPrefsApi.ICommunicationChannel>();

          angular.forEach(
            $scope.preferences.Categories,
            (cat: CommPrefsApi.ICommunicationCategory) => {
              angular.forEach(
                cat.Types,
                (type: CommPrefsApi.ICommunicationType) => {
                  angular.forEach(
                    type.Channels,
                    (chan: CommPrefsApi.ICommunicationChannel) => {
                      channels.push(chan);
                    }
                  );
                }
              );
            }
          );

          return channels;
        }
      },
    };
  },
];

export default function (module: ng.IModule): void {
  module.directive(directiveName, directive);
}
